/**
 * Linetype resolution utilities. Shared by layers/utils.ts (layer linetype)
 * and entities/properties.ts (entity linetype) — linetype isn't a
 * layer-specific concept, so it doesn't belong under drawing/layers.
 */

import type { DrawingModuleType } from "../DrawingSingleton";
import type { DrawingModule } from "../types/DrawingWeb";
import { deleteObj } from "../core/delete";
import { openAs } from "../core/oda";
import { OpenMode } from "../core/constants";

/**
 * Standard AutoCAD linetype names offered in the legend configurator.
 * Only ByLayer/ByBlock/Continuous are guaranteed to exist in every drawing
 * (see resolveLinetype below); the others depend on what's already loaded
 * in the drawing's linetype table (e.g. from its DWG template) and fall
 * back to Continuous otherwise. Hidden/Phantom (and their variants) were
 * removed: testé via create-linetype-test-legend.seed.ts, ils ne sont pas
 * chargés dans le gabarit de dessin actuel et retombaient silencieusement
 * sur Continuous.
 */
const NAMED_LINETYPES = [
  "Continuous",
  "Dashed",
  "Dashed2",
  "DashedX2",
  "Dot",
  "Dot2",
  "DotX2",
  "Dashdot",
  "Dashdot2",
  "DashdotX2",
  "Divide",
  "Divide2",
  "DivideX2",
  "Border",
  "Border2",
  "BorderX2",
  "Center",
  "Center2",
  "CenterX2",
] as const;

// Un calque référence toujours un linetype concret — ByLayer/ByBlock n'ont de sens que
// sur une entité (qui hérite alors du linetype de son calque/bloc). Continuous est la
// valeur par défaut d'un nouveau calque.
export const LAYER_LINETYPES = NAMED_LINETYPES;

// Une entité (NoteConfig) peut en plus hériter du calque (ByLayer, valeur par défaut)
// ou du bloc parent (ByBlock).
export const ENTITY_LINETYPES = [
  "ByLayer",
  "ByBlock",
  ...NAMED_LINETYPES,
] as const;

export type PredefinedLinetypeName = (typeof ENTITY_LINETYPES)[number];

/**
 * Resolve a linetype name to its OdDbObjectId in the current drawing.
 * ByLayer/ByBlock/Continuous always exist and are resolved directly.
 * Any other name must already be loaded in this drawing's linetype table
 * (e.g. from its DWG template) — unlike hatch patterns, there is no bundled
 * .lin library to auto-load arbitrary linetypes yet, so unknown names fall
 * back to Continuous with a console warning.
 */
export function resolveLinetype(
  module: DrawingModuleType,
  db: DrawingModule.OdDbDatabase,
  linetypeName: string,
): DrawingModule.OdDbObjectId {
  switch (linetypeName.toUpperCase()) {
    case "BYLAYER":
      return db.getLinetypeByLayerId();
    case "BYBLOCK":
      return db.getLinetypeByBlockId();
    case "CONTINUOUS":
      return db.getLinetypeContinuousId();
  }

  const linetypeTableId = db.getLinetypeTableId();
  const linetypeTable = openAs(
    linetypeTableId,
    module.OdDbLinetypeTable,
    OpenMode.kForRead,
  );
  deleteObj(linetypeTableId);

  const nameStr = new module.OdString();
  nameStr.set(linetypeName);

  let id: DrawingModule.OdDbObjectId;
  if (linetypeTable.has(nameStr)) {
    id = linetypeTable.getAt(nameStr, false);
  } else {
    console.warn(
      `[Linetype] "${linetypeName}" is not loaded in this drawing, falling back to Continuous`,
    );
    id = db.getLinetypeContinuousId();
  }

  deleteObj(nameStr);
  deleteObj(linetypeTable);
  return id;
}
